import type {
    CoreApplicationContext,
    CoreUser,
    GASettingKeys,
} from '@eventbrite/context-gen';
import type { GenericOverlayOptions } from '@eventbrite/eds-structure';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import type { ConversionBarProps } from '../components/ConversionBar/ConversionBar';
import type { EventHeroComponentProps } from '../components/EventHero/EventHero';
import type { EventDescriptionProps } from '../components/EventInfo/EventDescription/EventDescription';
import type { DateAndTimeProps } from '../components/EventInfo/EventDetails/DateAndTime/DateAndTime';
import type { LocationProps } from '../components/EventInfo/EventDetails/Location/Location';
import type { PerformersProps } from '../components/EventInfo/EventDetails/Performers/Performers';
import type { RefundPolicyProps } from '../components/EventInfo/EventDetails/RefundPolicy/RefundPolicy';
import type { FooterLinks } from '../components/Footer/Footer';
import type { NotificationBarSsrProps } from '../components/NotificationBar/NotificationBarSsr';
import type { OrganizerPanelLayoutProps } from '../components/OrganizerPanel/OrganizerPanelLayout';
import { ShareProps } from '../components/ShareEvent/ShareEventRoot';
import type { ShareInlineProps } from '../components/ShareInline/ShareInline';
import type { Tag } from '../components/Tags/Tags';

export interface Collection {
    id: string;
    name: string;
    url: string;
    summary: string;
    imageUrl?: string;
    upcomingEventsCount: number;
}

export interface CollectionResponse {
    image: { url: string };
    event_count: { current_future_published: number };
    absolute_url: string;
    id: string;
    image_id: string;
    name: string;
    organization_id: string;
    organizer_id: string;
    relative_url: string;
    slug: string;
    status: string;
    summary: string;
}

export interface EventCollectionsResponse {
    collections: CollectionResponse[];
    pagination: { object_count: number; has_more_items: boolean };
}

export interface LocalizedDateTime {
    utc: string;
    local: string;
    timezone: string;
}

export type ChildEventProps = {
    end: LocalizedDateTime;
    formatted_start_date: string;
    id: string;
    locale: string;
    short_start_date: string;
    start: LocalizedDateTime;
    status: string;
    url: string;
};

export interface Event {
    childEvents: Array<ChildEventProps>;
    end: LocalizedDateTime;
    hideEndDate: boolean;
    hideStartDate: boolean;
    id: string;
    isExpiredEvent: boolean;
    isOnlineEvent: boolean;
    isPreview: boolean;
    isSeriesParent: boolean;
    locale: string;
    name: string;
    ownerId?: string;
    source: string;
    start: LocalizedDateTime;
    url: string;
    hasCroppedLogo: boolean;
    isEnded: boolean;
    isLive: boolean;
    isProtected: boolean;
    startDateTime: string;
    startMonthAbbr: string;
    startDay: string;
    hasDonationTicketsAvailable?: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowRelatedEventsExpired: boolean;
}

export interface Organizer {
    id: string;
    name: string;
    nameTruncated: string;
    url: string;
    profilePicture: string;
    displayOrganizationName: string;
    description?: string;
    eventTitle?: string;
    orgWebsite?: string;
    orgFacebook?: string;
    orgTwitter?: string;
}

export interface Location {
    latitude: number;
    longitude: number;
}
export interface User extends CoreUser {
    id?: number;
    canManageEvent?: boolean;
}

export interface Settings {
    baseUrl: string;
    isMobile: boolean;
    featureFlags?: {
        enable_boost_banner_on_listings?: boolean;
        launchLinkToCreatorCollectionsOnListing?: boolean;
        enable_ssr_for_header?: boolean;
        enable_ssr_for_footer?: boolean;
    };
}

export interface GASettings extends GASettingKeys {
    category: string;
}

export interface ShareEventScopedProps {
    facebookOptions: {
        locale: string;
        version: string;
        page: string;
        xfbml: boolean;
        appId: string;
    };
    twitterHandle: string;
}

export interface FollowOrganizerScopedProps {
    isGDPRCountry: boolean;
}

export interface ContactOrganizerScopedProps {
    captchaKey: string;
}
export interface EventMapScopedProps {
    venueName: string;
    venueAddress: string;
    location: Location;
    gMapsApiKey: string;
    shouldDisplayMap: boolean;
}

export interface RemindMeScopedProps {
    isRemindedEvent: boolean;
}

export interface BookmarkScopedProps {
    isBookmarkedEvent: boolean;
}

export type SeriesDatesDropdownScopedProps = {
    childEvents: Array<ChildEventProps>;
    eventId: string;
    isOnlineEvent: boolean;
};

export type EventDateTimeProps = {
    end: LocalizedDateTime;
    hideEndDate: boolean;
    hideStartDate: boolean;
    isOnlineEvent: boolean;
    isSeriesParent: boolean;
    locale: string;
    start: LocalizedDateTime;
};
export interface OverlayOptions extends GenericOverlayOptions {
    kind: 'modal' | 'dialog';
}

export interface ModalOptions extends OverlayOptions {
    children: React.ReactNode;
    type?: string;
}
export interface DialogOptions extends OverlayOptions {
    message: string;
}

export interface OverlayProps {
    options: ModalOptions | DialogOptions | undefined;
}

export interface NotificationOptions {
    children: React.ReactNode;
    hasCloseButton: boolean;
    isClosing: boolean;
    isNotificationSticky?: boolean;
    onClose?: () => void;
    position?: string;
    shouldPersist?: boolean;
    shouldStick: boolean;
    stickyTopHeight?: number;
}

export interface NotificationProps {
    fullWidth?: string;
    options: NotificationOptions | undefined;
}

export type MainBodyBackgroundScopedProps = {
    croppedLogoUrl50?: string;
};

export enum Environment {
    DEV = 'dev',
    QA = 'qa',
    PROD = 'prod',
}

export type EventDetailsProps = {
    performers: PerformersProps;
    location: LocationProps;
    onlineEvent: { url?: string };
    refund: RefundPolicyProps;
    dates: DateAndTimeProps;
};

export interface AppProps extends CoreApplicationContext {
    components: {
        conversionBar: ConversionBarProps;
        shareEvent: ShareEventScopedProps;
        followOrganizer: FollowOrganizerScopedProps;
        contactOrganizer: ContactOrganizerScopedProps;
        eventMap: EventMapScopedProps;
        remindMe: RemindMeScopedProps;
        bookmark: BookmarkScopedProps;
        eventDescription: EventDescriptionProps;
        eventDetails: EventDetailsProps;
        tags: Array<Tag>;
        shareInline: ShareInlineProps;
        organizerPanel: OrganizerPanelLayoutProps;
        eventHero: EventHeroComponentProps;
        mainBodyBackground: MainBodyBackgroundScopedProps;
        notificationBar: NotificationBarSsrProps;
        relatedEventsExpiredPage: RelatedEventsExpiredPage;
    };
    event: Event;
    gaSettings: GASettings;
    tracking: TrackingProps;
    settings: Settings;
    app_name: string;
    app_version: string;
    organizer: Organizer;
    user: User;
    footerLinks: FooterLinks;
}

export type RelatedEventsExpiredPage = {
    relatedEvents: DiscoveryEvent[];
    shareComponentProps: ShareProps;
};

export interface ControlledProps extends AppProps {
    notificationOptions: NotificationOptions | undefined;
    overlayOptions: OverlayOptions | undefined;
    creatorCollections?: CollectionResponse[];
}

export type EnhancedAppProps = AppProps & {
    creatorCollections?: CollectionResponse[];
};

interface OrganizerData extends Organizer {
    followedByYou: boolean;
    numFollowers?: number;
    numUpcomingEvents?: number;
}
export interface FollowOrganizerProps {
    organizerId: string;
    gaSettings: GASettings;
    gaCategory: string;
    organizers: string[];
    isAuthenticated: boolean;
    userId: string;
    isGDPRCountry: boolean;
    size?: string;
    organizerData?: {
        [key: string]: OrganizerData;
    };
    renderFollowCount?(followCount: string): JSX.Element;
}

export type RemindMeProps = {
    eventUrl: string;
    eventId: string;
    eventName: string;
    isRemindedEvent: boolean;
    gaSettings: GASettings;
    organizerId: string;
};

export type OrganizerPanelListingsProps = {
    organizerName: string;
    organizerId: string;
    organizerProfilePicture: string;
    organizerUrl: string;
    organizerDescription?: string;
    organizerEventTitle?: string;
    captchaKey: string;
    isAuthenticated?: boolean;
    eventName: string;
    eventId: string;
    gaCategory: string;
    orgFacebook?: string;
    orgTwitter?: string;
    orgWebsite?: string;
};

export type SocialLinksProps = {
    orgTwitter?: string;
    orgFacebook?: string;
    orgWebsite?: string;
};

export type NotificationBarProps = {
    isHidden: boolean;
    type?: string;
    hasCloseButton?: boolean;
    children?: React.ReactNode;
};

export type TrackingProps = {
    debugging: boolean;
    enabled: boolean;
    environment: Environment;
    launchSupportMultipleFacebookPixels: boolean;
    shouldFireTrackingBeacon: boolean;
    shouldTrackEventView: boolean;
    staticEventCache: boolean;
    trackingBeaconsSerialized: any;
};

// GA tracking dropdown
export enum GACodes {
    GA_ACTION_BOOKMARK_ADD = 'BookmarkEvent',
    GA_ACTION_BOOKMARK_REMOVE = 'UnbookmarkEvent',
    GA_ACTION_CHECKOUT = 'NewListingTicketsClick',
    GA_ACTION_COLLECTIONS_MODAL_LINK_CLICK = 'AllCollectionsModalLinkClick',
    GA_ACTION_COLLECTION_PAGE_LINK_CLICK = 'CollectionPageLinkClick',
    GA_ACTION_CONTACT_ORG_ATTEMPT_EVENT = 'ContactOrgAttempt',
    GA_ACTION_DIRECTIONS = 'Directions',
    GA_ACTION_MAP_INTERACTION = 'MapInteract',
    GA_ACTION_SHARE_EVENT = 'Share',
    GA_ACTION_REMINDME = 'RemindMeButton',
    GA_ACTION_UNREMINDME = 'TurnOffRemindMeButton',
    GA_ACTION_SHOW_LIKE_GDPR_MODAL = 'ShowLikeGDPRModal',
    GA_ACTION_SOCIAL_LINK_CLICK = 'ViewOrg',
    GA_ACTION_SELECT_DATE_ATTEMPT = 'SelectDateAttempt',
    GA_ACTION_SELECT_DATE = 'SelectDate',
    GA_ACTION_RSVP_LOADED = 'RSVPLoaded',
    GA_ACTION_RSVP_YES_INTENT = 'RSVPYesIntent',
    GA_ACTION_RSVP_MAYBE = 'RSVPMaybe',
    GA_ACTION_RSVP_NO = 'RSVPNo',
    GA_CATEGORY_COLLECTION_CARD = 'collectioncard',
    GA_CATEGORY_CREATOR_COLLECTIONS_LISTING = 'creator-collections-listing',
    GA_CATEGORY_EVENT_CARD = 'eventcard',
    GA_CATEGORY_LISTING = 'listing',
    GA_LABEL_OPEN_MODAL = 'open-modal',
    GA_LABEL_SHELF = 'shelf',
    GA_LABEL_EVENT_DETAILS = 'event-details',
    GA_LABEL_BIKE = 'bicycling',
    GA_LABEL_BUS = 'transit',
    GA_LABEL_DRIVE = 'drive',
    GA_LABEL_WALK = 'walk',
    GA_LABEL_ZOOM = 'zoom-change',
    GA_LABEL_FACEBOOK = 'fb',
    GA_LABEL_TWITTER = 'tw',
    GA_LABEL_WEBSITE = 'website',
    GA_LABEL_BOOKMARK_EVENTS_REMINDER_OK = 'LikeGDPRConfirmationOk',
    GA_LABEL_BOOKMARK_EVENTS_REMINDER_NO = 'LikeGDPRConfirmationNo',
    GA_LABEL_SECONDARY = 'secondary',
    GA_LABEL_RSVP_MAYBE_MSG_SENT = 'RSVPMaybeMessageSent',
    GA_LABEL_RSVP_NO_MSG_SENT = 'RSVPNoMessageSent',
}

// Affiliate codes
export enum AffiliateCodes {
    AFF_RELATED_EVENTS_LIVE = 'erellivmlt', // Related events for live source events
    AFF_RELATED_EVENTS_SAME_ORG = 'erelpanelorg', // Related events for the same organizer
    AFF_RELATED_EVENTS_SAME_COLLECTION = 'odclrlmeitc', // Related events in the same collection
    AFF_RELATED_EVENTS_EXPIRED = 'erelexpmlt', // Related events on expired event pages
    AFF_COLLECTIONS_FEATURING_THIS_EVENT = 'odclrlmctfte', // More collections featuring this event
}
